/**
 * MobileCombatHUD Component
 *
 * Minimal HUD bar (36px) for mobile combat mode.
 * Shows menu button, round number, and series score.
 *
 * @module components/combat/MobileCombatHUD
 */

import React from 'react';
import styles from './MobileCombatHUD.module.css';

export interface MobileCombatHUDProps {
  /** Current round number */
  roundNumber: number;
  /** Series win/loss record, null if first round */
  seriesRecord: {
    player1Wins: number;
    player2Wins: number;
    draws: number;
  } | null;
  /** Current player ID to determine which score is "yours" */
  currentPlayerId: 'player1' | 'player2';
  /** Callback when menu button is pressed */
  onMenuOpen: () => void;
}

/**
 * MobileCombatHUD displays a compact HUD bar for mobile combat viewing.
 *
 * Layout: [☰ Menu] [R1] [Score: 2-1]
 *
 * @example
 * ```tsx
 * <MobileCombatHUD
 *   roundNumber={1}
 *   seriesRecord={{ player1Wins: 2, player2Wins: 1, draws: 0 }}
 *   currentPlayerId="player1"
 *   onMenuOpen={() => setMenuOpen(true)}
 * />
 * ```
 */
export function MobileCombatHUD({
  roundNumber,
  seriesRecord,
  currentPlayerId,
  onMenuOpen,
}: MobileCombatHUDProps) {
  const yourWins = seriesRecord
    ? currentPlayerId === 'player1'
      ? seriesRecord.player1Wins
      : seriesRecord.player2Wins
    : 0;

  const opponentWins = seriesRecord
    ? currentPlayerId === 'player1'
      ? seriesRecord.player2Wins
      : seriesRecord.player1Wins
    : 0;

  return (
    <div className={styles.hud}>
      {/* Menu Button */}
      <button
        className={styles.menuButton}
        onClick={onMenuOpen}
        aria-label="Open combat menu"
        type="button"
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
        >
          <rect x="2" y="4" width="16" height="2" rx="1" />
          <rect x="2" y="9" width="16" height="2" rx="1" />
          <rect x="2" y="14" width="16" height="2" rx="1" />
        </svg>
      </button>

      {/* Round Number */}
      <span className={styles.roundNumber}>R{roundNumber}</span>

      {/* Divider */}
      <span className={styles.divider}>|</span>

      {/* Series Score */}
      {seriesRecord ? (
        <div className={styles.score}>
          <span className={styles.yourScore}>{yourWins}</span>
          <span className={styles.scoreDash}>-</span>
          <span className={styles.opponentScore}>{opponentWins}</span>
        </div>
      ) : (
        <span className={styles.firstRound}>First Battle</span>
      )}
    </div>
  );
}

export default MobileCombatHUD;
